import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../../services/api';

// ===== Types =====

interface CampaignData {
  id?: string;
  name: string;
  subject: string;
  body_html: string;
  sender_type: string;
  sender_reply_to: string;
  recipient_group: string;
  recipient_filter: Record<string, any> | null;
  recipient_ids: number[] | null;
  status?: string;
}

type GreetingType = 'first_name' | 'farm_name' | 'custom' | 'generic';

interface RecipientRow {
  id: number;
  name: string;
  email: string;
  contact_person: string;
  customer_type: string;
  greeting_type: GreetingType;
  greeting_name: string;
  is_fallback: boolean;
  selected: boolean;
  sent_status?: string | null;  // 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'failed' | null
  sent_at?: string | null;
}

interface CampaignComposeProps {
  campaignId?: string | null;
  onBack: () => void;
  onSaved: (id: string) => void;
}

// ===== Constants =====

const SENDER_OPTIONS = [
  { value: 'brad', label: 'Brad Murphy (brad@theredbridgefarm.com)' },
  { value: 'noreply', label: 'No-Reply (noreply@theredbridgefarm.com)' },
  { value: 'office', label: 'Office (office@theredbridgefarm.com)' },
];

const GROUP_OPTIONS = [
  { value: 'all', label: 'All Customers' },
  { value: 'wholesale', label: 'Wholesale Farms' },
  { value: 'retail_partners', label: 'Retail Partners' },
  { value: 'local_will_call', label: 'Local Will Call' },
  { value: 'bridge_club', label: 'Bridge Club Members' },
  { value: 'farmhand', label: 'Farmhand Users' },
];

const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First Name' },
  { tag: '{{farm_name}}', label: 'Farm Name' },
  { tag: '{{last_order_date}}', label: 'Last Order' },
  { tag: '{{booking_link:portal-walkthrough}}', label: 'Booking Link' },
  { tag: '{{unsubscribe_link}}', label: 'Unsubscribe' },
];

const TYPE_LABELS: Record<string, string> = {
  WHOLESALE_FARM: 'Wholesale',
  RETAIL_PARTNER: 'Retail',
  LOCAL_WILL_CALL: 'Local',
  OTHER: 'Other',
  CJ_CUSTOMER: 'CJ',
};

// ===== Component =====

const CampaignCompose: React.FC<CampaignComposeProps> = ({ campaignId, onBack, onSaved }) => {
  // Campaign data
  const [data, setData] = useState<CampaignData>({
    name: '',
    subject: '',
    body_html: '',
    sender_type: 'brad',
    sender_reply_to: '',
    recipient_group: 'all',
    recipient_filter: null,
    recipient_ids: null,
  });
  const [loading, setLoading] = useState(!!campaignId);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Recipient management
  const [recipientRows, setRecipientRows] = useState<RecipientRow[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [hasManualChanges, setHasManualChanges] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [sortField, setSortField] = useState<'name' | 'customer_type' | 'greeting_name'>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [editingGreetingId, setEditingGreetingId] = useState<number | null>(null);
  const [editingGreetingValue, setEditingGreetingValue] = useState('');

  // Add recipient search
  const [addSearch, setAddSearch] = useState('');
  const [addResults, setAddResults] = useState<any[]>([]);
  const [addLoading, setAddLoading] = useState(false);
  const addSearchTimer = useRef<ReturnType<typeof setTimeout>>();

  // Preview/schedule
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewRecipients, setPreviewRecipients] = useState<Array<{id: string; name: string; email_address: string; has_merge_fields: boolean; has_password: boolean}>>([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewMergeData, setPreviewMergeData] = useState<Record<string, string>>({});
  const [previewSearch, setPreviewSearch] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [showRawHtml, setShowRawHtml] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // Video block state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState('');

  // Send Test state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testSearch, setTestSearch] = useState('');
  const [testSending, setTestSending] = useState('');
  const [testSuccess, setTestSuccess] = useState('');
  const [testRecipients, setTestRecipients] = useState<Array<{id: string; name: string; email_address: string; has_merge_fields: boolean; has_password: boolean}>>([]);

  // ===== Computed values =====

  const selectedCount = useMemo(() => recipientRows.filter(r => r.selected).length, [recipientRows]);
  const totalCount = recipientRows.length;
  const greetingResolved = useMemo(() => recipientRows.filter(r => r.selected && !r.is_fallback).length, [recipientRows]);
  const greetingFallback = useMemo(() => recipientRows.filter(r => r.selected && r.is_fallback).length, [recipientRows]);
  const previouslySentCount = useMemo(() => recipientRows.filter(r => r.sent_status && r.sent_status !== 'queued' && r.sent_status !== 'failed').length, [recipientRows]);

  const filteredRows = useMemo(() => {
    let rows = [...recipientRows];
    if (recipientSearch) {
      const q = recipientSearch.toLowerCase();
      rows = rows.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.greeting_name.toLowerCase().includes(q)
      );
    }
    rows.sort((a, b) => {
      const aVal = (a[sortField] || '').toLowerCase();
      const bVal = (b[sortField] || '').toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortAsc ? cmp : -cmp;
    });
    return rows;
  }, [recipientRows, recipientSearch, sortField, sortAsc]);

  const isReadOnly = data.status === 'sending';

  // ===== Load campaign =====

  useEffect(() => {
    if (campaignId) {
      loadCampaign();
    }
  }, [campaignId]);

  const loadCampaign = async () => {
    try {
      const res = await api.get(`/admin/campaigns/${campaignId}/`);
      setData(res.data);
      // If campaign has saved recipients, load their details
      if (res.data.recipient_ids && res.data.recipient_ids.length > 0) {
        try {
          const recipRes = await api.post(`/admin/campaigns/${campaignId}/resolve-recipients/`, {
            recipient_ids: res.data.recipient_ids,
          });
          setRecipientRows(
            recipRes.data.recipients.map((r: any) => ({ ...r, selected: true }))
          );
        } catch { /* table will be empty, user can re-load */ }
      }
    } catch (err) {
      setError('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  // ===== Helpers =====

  const showMsg = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  // ===== Save =====

  const handleSave = async (): Promise<string | null> => {
    if (!data.name || !data.subject) {
      setError('Campaign name and subject are required');
      return null;
    }
    setError('');
    setSaving(true);
    try {
      if (editorRef.current && !showRawHtml) {
        data.body_html = editorRef.current.innerHTML;
      }

      // Sync selected recipient IDs into save payload
      const savePayload = { ...data };
      if (recipientRows.length > 0) {
        savePayload.recipient_ids = recipientRows.filter(r => r.selected).map(r => r.id);
      }

      let res;
      if (savePayload.id) {
        res = await api.put(`/admin/campaigns/${savePayload.id}/`, savePayload);
      } else {
        res = await api.post('/admin/campaigns/', savePayload);
      }
      setData(res.data);
      showMsg('Campaign saved!');
      onSaved(res.data.id);
      return res.data.id;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // ===== Recipient management =====

  const handleLoadRecipients = async () => {
    if (hasManualChanges && recipientRows.length > 0) {
      if (!window.confirm('Loading a new group will replace your current recipient list. Continue?')) return;
    }

    setLoadingRecipients(true);
    setError('');
    try {
      // Ensure campaign is saved first
      let id = data.id;
      if (!id) {
        id = await handleSave() || undefined;
      }
      if (!id) return;

      const res = await api.post(`/admin/campaigns/${id}/resolve-recipients/`, {
        recipient_group: data.recipient_group,
      });
      setRecipientRows(
        res.data.recipients.map((r: any) => ({ ...r, selected: true }))
      );
      setHasManualChanges(false);
      setRecipientSearch('');
      setAddSearch('');
      setAddResults([]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const toggleRecipient = (id: number) => {
    setRecipientRows(prev => prev.map(r => r.id === id ? { ...r, selected: !r.selected } : r));
    setHasManualChanges(true);
  };

  const toggleAll = (selected: boolean) => {
    setRecipientRows(prev => prev.map(r => ({ ...r, selected })));
    setHasManualChanges(true);
  };

  const clearPreviousSends = () => {
    setRecipientRows(prev => prev.map(r => {
      if (r.sent_status && r.sent_status !== 'queued' && r.sent_status !== 'failed') {
        return { ...r, selected: false };
      }
      return r;
    }));
    setHasManualChanges(true);
  };

  const handleSort = (field: 'name' | 'customer_type' | 'greeting_name') => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sortArrow = (field: string) => sortField === field ? (sortAsc ? ' \u25B2' : ' \u25BC') : '';

  // ===== Greeting name/type editing =====

  const saveGreetingName = async (customerId: number, name: string) => {
    if (!name.trim()) return;
    try {
      const res = await api.patch(`/admin/customers/${customerId}/greeting-name/`, { greeting_name: name.trim() });
      const resolved = res.data.resolved_greeting;
      setRecipientRows(prev => prev.map(r =>
        r.id === customerId ? { ...r, greeting_name: resolved, is_fallback: resolved === 'there' } : r
      ));
      setEditingGreetingId(null);
    } catch {
      setError('Failed to save greeting name');
    }
  };

  const saveGreetingType = async (customerId: number, gt: GreetingType) => {
    try {
      const payload: Record<string, string> = { greeting_type: gt };
      const res = await api.patch(`/admin/customers/${customerId}/greeting-name/`, payload);
      const resolved = res.data.resolved_greeting;
      setRecipientRows(prev => prev.map(r =>
        r.id === customerId ? { ...r, greeting_type: gt, greeting_name: resolved, is_fallback: resolved === 'there' } : r
      ));
    } catch {
      setError('Failed to save greeting type');
    }
  };

  // ===== Add recipient search =====

  const handleAddSearchInput = (q: string) => {
    setAddSearch(q);
    clearTimeout(addSearchTimer.current);
    if (q.length < 2) { setAddResults([]); return; }
    addSearchTimer.current = setTimeout(async () => {
      setAddLoading(true);
      try {
        const excludeIds = recipientRows.map(r => r.id).join(',');
        const res = await api.get(`/admin/campaigns/search-customers/?q=${encodeURIComponent(q)}&exclude=${excludeIds}`);
        setAddResults(res.data);
      } catch { /* ignore */ }
      finally { setAddLoading(false); }
    }, 300);
  };

  const handleAddRecipient = (customer: any) => {
    const newRow: RecipientRow = {
      id: customer.id,
      name: customer.name,
      email: customer.email,
      contact_person: customer.contact_person || '',
      customer_type: customer.customer_type,
      greeting_type: customer.greeting_type || 'first_name',
      greeting_name: customer.greeting_name || 'there',
      is_fallback: customer.is_fallback !== false,
      selected: true,
    };
    setRecipientRows(prev => [newRow, ...prev]);
    setAddResults(prev => prev.filter(r => r.id !== customer.id));
    setHasManualChanges(true);
  };

  // ===== Preview / Test / Send =====

  const handlePreview = async () => {
    const id = data.id || await handleSave();
    if (!id) return;
    try {
      if (editorRef.current && !showRawHtml) {
        data.body_html = editorRef.current.innerHTML;
      }
      await api.put(`/admin/campaigns/${id}/`, { ...data, recipient_ids: recipientRows.length > 0 ? recipientRows.filter(r => r.selected).map(r => r.id) : data.recipient_ids });

      // Load recipient list for preview picker
      const detailRes = await api.get(`/admin/campaigns/${id}/`);
      const recipients = (detailRes.data.recipients || []).map((r: any) => ({
        id: r.id,
        name: r.name || r.customer_name || 'Unknown',
        email_address: r.email_address,
        has_merge_fields: r.has_merge_fields || false,
        has_password: r.has_password || false,
      }));
      setPreviewRecipients(recipients);
      setPreviewIndex(0);
      setPreviewSearch('');

      // Load preview for first recipient (or generic)
      const previewPayload: any = {};
      if (recipients.length > 0) {
        previewPayload.recipient_id = recipients[0].id;
      }
      const res = await api.post(`/admin/campaigns/${id}/preview/`, previewPayload);
      setPreviewHtml(res.data.html);
      setPreviewMergeData(res.data.merge_data || {});
      setShowPreview(true);
    } catch {
      setError('Failed to generate preview');
    }
  };

  const loadPreviewForRecipient = async (recipientId: string, index: number) => {
    if (!data.id) return;
    setPreviewLoading(true);
    setPreviewIndex(index);
    try {
      const res = await api.post(`/admin/campaigns/${data.id}/preview/`, { recipient_id: recipientId });
      setPreviewHtml(res.data.html);
      setPreviewMergeData(res.data.merge_data || {});
    } catch {
      setError('Failed to load preview for recipient');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleOpenSendTest = async () => {
    const id = data.id || await handleSave();
    if (!id) return;
    try {
      if (editorRef.current && !showRawHtml) {
        data.body_html = editorRef.current.innerHTML;
      }
      await api.put(`/admin/campaigns/${id}/`, { ...data, recipient_ids: recipientRows.length > 0 ? recipientRows.filter(r => r.selected).map(r => r.id) : data.recipient_ids });

      // Load recipients for the test picker
      const detailRes = await api.get(`/admin/campaigns/${id}/`);
      const recipients = (detailRes.data.recipients || []).map((r: any) => ({
        id: r.id,
        name: r.name || r.customer_name || 'Unknown',
        email_address: r.email_address,
        has_merge_fields: r.has_merge_fields || false,
        has_password: r.has_password || false,
      }));
      setTestRecipients(recipients);
      setTestSearch('');
      setTestSuccess('');
      setShowTestModal(true);
    } catch {
      setError('Save campaign first, then send test');
    }
  };

  const handleSendTestToRecipient = async (recipientId: string, recipientName: string) => {
    if (!data.id) return;
    setTestSending(recipientId);
    setTestSuccess('');
    try {
      const res = await api.post(`/admin/campaigns/${data.id}/send-test/`, {
        recipient_id: recipientId,
      });
      setTestSuccess(`Test sent to ${res.data.sent_to} (${recipientName})`);
    } catch (err: any) {
      setTestSuccess(`Failed: ${err.response?.data?.error || 'Unknown error'}`);
    } finally {
      setTestSending('');
    }
  };

  const handleSendNow = async () => {
    if (!data.id) return;
    const selected = recipientRows.filter(r => r.selected);
    if (selected.length === 0) {
      setError('No recipients selected. Load recipients first.');
      return;
    }
    if (!window.confirm(`Send this campaign to ${selected.length} recipients? This cannot be undone.`)) return;

    setSending(true);
    setError('');
    try {
      if (editorRef.current && !showRawHtml) {
        data.body_html = editorRef.current.innerHTML;
      }
      // Save with final selected recipient IDs
      const savePayload = { ...data, recipient_ids: selected.map(r => r.id) };
      await api.put(`/admin/campaigns/${data.id}/`, savePayload);
      const res = await api.post(`/admin/campaigns/${data.id}/send/`);
      if (res.data.sending) {
        // Background send — emails are being sent asynchronously
        showMsg(res.data.message || `Sending to ${res.data.total_recipients} recipients...`);
        setData(prev => ({ ...prev, status: 'sending' }));
      } else if (res.data.sent_count > 0) {
        const extra = [];
        if (res.data.failed_count > 0) extra.push(`${res.data.failed_count} failed`);
        if (res.data.skipped_count > 0) extra.push(`${res.data.skipped_count} skipped`);
        showMsg(`Sent to ${res.data.sent_count} recipients` + (extra.length ? ` (${extra.join(', ')})` : ''));
        setData(prev => ({ ...prev, status: 'sent' }));
      } else {
        const reasons = res.data.skip_reasons?.length
          ? res.data.skip_reasons.join('; ')
          : 'Check server logs.';
        setError(`No emails were sent. ${res.data.failed_count} failed, ${res.data.skipped_count || 0} skipped. ${reasons}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async () => {
    if (!data.id || !scheduleDate || !scheduleTime) return;
    try {
      // Save recipients first
      if (recipientRows.length > 0) {
        await api.put(`/admin/campaigns/${data.id}/`, {
          ...data,
          recipient_ids: recipientRows.filter(r => r.selected).map(r => r.id),
        });
      }
      const scheduled_at = `${scheduleDate}T${scheduleTime}:00`;
      await api.post(`/admin/campaigns/${data.id}/schedule/`, { scheduled_at });
      showMsg('Campaign scheduled!');
      setShowSchedule(false);
      setData(prev => ({ ...prev, status: 'scheduled' }));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to schedule');
    }
  };

  // ===== Editor helpers =====

  const insertMergeTag = (tag: string) => {
    if (showRawHtml) {
      setData(prev => ({ ...prev, body_html: prev.body_html + tag }));
    } else if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, tag);
    }
  };

  const execFormat = (cmd: string, value?: string) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
  };

  const handleInsertVideo = async () => {
    if (!videoUrl.trim()) return;
    setVideoLoading(true);
    setVideoError('');
    try {
      const res = await api.post('/admin/campaigns/video-thumbnail/', { youtube_url: videoUrl.trim() });
      const { thumbnail_url, video_title, youtube_url: canonicalUrl } = res.data;
      const title = video_title || 'Watch Video';

      // Build email-safe HTML block
      const videoHtml = `
<div style="text-align:center;margin:20px 0;" data-video-block="true">
  <a href="${canonicalUrl}" target="_blank" style="display:inline-block;text-decoration:none;">
    <img src="${thumbnail_url}" width="560" alt="${title.replace(/"/g, '&quot;')}" style="border-radius:8px;max-width:100%;height:auto;" />
    <p style="margin:8px 0 0;color:#666;font-size:14px;">&#9654; ${title}</p>
  </a>
</div>
<p></p>`;

      if (showRawHtml) {
        setData(prev => ({ ...prev, body_html: prev.body_html + videoHtml }));
      } else if (editorRef.current) {
        editorRef.current.focus();
        // Insert at cursor or append
        document.execCommand('insertHTML', false, videoHtml);
      }

      setShowVideoModal(false);
      setVideoUrl('');
    } catch (err: any) {
      setVideoError(err.response?.data?.error || 'Failed to generate video thumbnail');
    } finally {
      setVideoLoading(false);
    }
  };

  // ===== Render =====

  if (loading) {
    return <p style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>Loading...</p>;
  }

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#2D5A27', cursor: 'pointer', padding: '0.5rem 0', marginBottom: '1rem', fontSize: '0.9rem' }}>
        &larr; Back to Campaigns
      </button>

      {message && (
        <div style={{ padding: '0.75rem 1rem', background: '#f0f7ee', color: '#2D5A27', borderRadius: '6px', marginBottom: '1rem', fontWeight: 500 }}>
          {message}
        </div>
      )}
      {error && (
        <div style={{ padding: '0.75rem 1rem', background: '#fff5f5', color: '#8B0000', borderRadius: '6px', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* ===== Main Grid: Content + Actions Sidebar ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '1.5rem', alignItems: 'start' }}>
        {/* Left Panel — Who + What */}
        <div>
          {/* Campaign Name, Subject, Sender */}
          <div style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 500, fontSize: '0.9rem' }}>Campaign Name (internal)</label>
              <input
                type="text"
                value={data.name}
                onChange={e => setData(prev => ({ ...prev, name: e.target.value }))}
                disabled={isReadOnly}
                placeholder="e.g., February Feed Sale"
                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 500, fontSize: '0.9rem' }}>Subject Line</label>
              <input
                type="text"
                value={data.subject}
                onChange={e => setData(prev => ({ ...prev, subject: e.target.value }))}
                disabled={isReadOnly}
                placeholder="e.g., New Spring Feed Lineup"
                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 500, fontSize: '0.9rem' }}>Sender</label>
              <select
                value={data.sender_type}
                onChange={e => setData(prev => ({ ...prev, sender_type: e.target.value }))}
                disabled={isReadOnly}
                style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}
              >
                {SENDER_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Recipient Group + Load */}
          <div style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.9rem' }}>Recipients</label>
                <div style={{ display: 'flex', gap: '0.5rem 1.25rem', flexWrap: 'wrap' }}>
                  {GROUP_OPTIONS.map(o => (
                    <label key={o.value} style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input
                        type="radio"
                        name="recipient_group"
                        value={o.value}
                        checked={data.recipient_group === o.value}
                        onChange={e => setData(prev => ({ ...prev, recipient_group: e.target.value }))}
                        disabled={isReadOnly}
                        style={{ marginRight: '0.35rem' }}
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
              </div>
              <button
                onClick={handleLoadRecipients}
                disabled={isReadOnly || loadingRecipients}
                style={{
                  padding: '0.5rem 1.25rem',
                  border: 'none',
                  borderRadius: '4px',
                  background: '#2D5A27',
                  color: 'white',
                  cursor: loadingRecipients ? 'wait' : 'pointer',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {loadingRecipients ? 'Loading...' : 'Load Recipients'}
              </button>
            </div>
          </div>

          {/* Recipient Table */}
          {recipientRows.length > 0 && (
            <div style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1rem' }}>
              {/* Summary bar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                    {selectedCount} of {totalCount} selected
                  </span>
                  <span style={{ color: '#16a34a', fontSize: '0.85rem' }}>
                    {greetingResolved} have greeting names
                  </span>
                  {greetingFallback > 0 && (
                    <span style={{ color: '#d97706', fontSize: '0.85rem' }}>
                      {greetingFallback} will get &quot;Hey there,&quot;
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => toggleAll(true)}
                    disabled={isReadOnly}
                    style={{ padding: '0.3rem 0.6rem', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '0.8rem' }}
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => toggleAll(false)}
                    disabled={isReadOnly}
                    style={{ padding: '0.3rem 0.6rem', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '0.8rem' }}
                  >
                    Deselect All
                  </button>
                  {previouslySentCount > 0 && (
                    <button
                      onClick={clearPreviousSends}
                      disabled={isReadOnly}
                      style={{ padding: '0.3rem 0.6rem', border: '1px solid #f59e0b', borderRadius: '4px', background: '#fffbeb', color: '#92400e', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 }}
                    >
                      Clear {previouslySentCount} Already Sent
                    </button>
                  )}
                </div>
              </div>

              {/* Search + Add Recipient */}
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <input
                    type="text"
                    value={recipientSearch}
                    onChange={e => setRecipientSearch(e.target.value)}
                    placeholder="Search loaded recipients..."
                    style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '180px', position: 'relative' }}>
                  <input
                    type="text"
                    value={addSearch}
                    onChange={e => handleAddSearchInput(e.target.value)}
                    placeholder="Add recipient — search by name or email..."
                    style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #2D5A27', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                  {(addResults.length > 0 || addLoading) && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                      background: 'white', border: '1px solid #ddd', borderRadius: '0 0 4px 4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '200px', overflow: 'auto',
                    }}>
                      {addLoading && <div style={{ padding: '0.5rem 0.75rem', color: '#999', fontSize: '0.8rem' }}>Searching...</div>}
                      {addResults.map(c => (
                        <div
                          key={c.id}
                          onClick={() => handleAddRecipient(c)}
                          style={{
                            padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem',
                            borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f0f7ee')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                        >
                          <span><strong>{c.name}</strong> — {c.email}</span>
                          <span style={{ color: '#999', fontSize: '0.75rem' }}>{TYPE_LABELS[c.customer_type] || c.customer_type}</span>
                        </div>
                      ))}
                      {!addLoading && addResults.length === 0 && addSearch.length >= 2 && (
                        <div style={{ padding: '0.5rem 0.75rem', color: '#999', fontSize: '0.8rem' }}>No matching customers found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Table */}
              <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={{ ...thStyle, width: '36px' }}>
                        <input
                          type="checkbox"
                          checked={selectedCount === totalCount && totalCount > 0}
                          onChange={e => toggleAll(e.target.checked)}
                          disabled={isReadOnly}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                      <th
                        style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('name')}
                      >
                        Customer Name{sortArrow('name')}
                      </th>
                      <th style={{ ...thStyle, width: '120px' }}>Greeting Type</th>
                      <th
                        style={{ ...thStyle, cursor: 'pointer', userSelect: 'none', width: '130px' }}
                        onClick={() => handleSort('greeting_name')}
                      >
                        Greeting{sortArrow('greeting_name')}
                      </th>
                      <th style={thStyle}>Email</th>
                      <th
                        style={{ ...thStyle, cursor: 'pointer', userSelect: 'none', width: '80px' }}
                        onClick={() => handleSort('customer_type')}
                      >
                        Type{sortArrow('customer_type')}
                      </th>
                      <th style={{ ...thStyle, width: '70px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(r => {
                      const wasSent = r.sent_status && r.sent_status !== 'queued' && r.sent_status !== 'failed';
                      return (
                      <tr
                        key={r.id}
                        style={{
                          background: wasSent ? '#f9fafb' : r.selected ? 'white' : '#fafafa',
                          opacity: r.selected ? 1 : wasSent ? 0.55 : 0.5,
                        }}
                      >
                        <td style={tdStyle}>
                          <input
                            type="checkbox"
                            checked={r.selected}
                            onChange={() => toggleRecipient(r.id)}
                            disabled={isReadOnly}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>{r.name}</td>
                        <td style={tdStyle}>
                          <select
                            value={r.greeting_type || 'first_name'}
                            onChange={e => saveGreetingType(r.id, e.target.value as GreetingType)}
                            disabled={isReadOnly}
                            style={{
                              padding: '0.15rem 0.2rem', border: '1px solid #ddd', borderRadius: '3px',
                              fontSize: '0.75rem', background: 'white', cursor: isReadOnly ? 'default' : 'pointer',
                              width: '100%', color: '#333',
                            }}
                          >
                            <option value="first_name">First Name</option>
                            <option value="farm_name">Farm Name</option>
                            <option value="custom">Custom</option>
                            <option value="generic">Hey there</option>
                          </select>
                        </td>
                        <td style={tdStyle}>
                          {r.greeting_type === 'custom' || r.greeting_type === 'first_name' ? (
                            editingGreetingId === r.id ? (
                              <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                <input
                                  value={editingGreetingValue}
                                  onChange={e => setEditingGreetingValue(e.target.value)}
                                  placeholder={r.greeting_type === 'custom' ? 'Custom greeting' : 'First name'}
                                  autoFocus
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') saveGreetingName(r.id, editingGreetingValue);
                                    if (e.key === 'Escape') setEditingGreetingId(null);
                                  }}
                                  onBlur={() => {
                                    if (editingGreetingValue.trim()) saveGreetingName(r.id, editingGreetingValue);
                                    else setEditingGreetingId(null);
                                  }}
                                  style={{ width: '80px', padding: '0.15rem 0.3rem', border: '1px solid #2D5A27', borderRadius: '3px', fontSize: '0.8rem', outline: 'none' }}
                                />
                              </div>
                            ) : (
                              <span
                                onClick={() => { if (!isReadOnly) { setEditingGreetingId(r.id); setEditingGreetingValue(r.is_fallback ? '' : r.greeting_name); } }}
                                style={{
                                  cursor: isReadOnly ? 'default' : 'pointer',
                                  color: r.is_fallback ? '#d97706' : '#16a34a',
                                  background: r.greeting_type === 'first_name' && r.is_fallback ? '#fef9c3' : 'transparent',
                                  padding: r.greeting_type === 'first_name' && r.is_fallback ? '0 0.25rem' : '0',
                                  borderRadius: '2px',
                                  borderBottom: isReadOnly ? 'none' : '1px dashed currentColor',
                                  paddingBottom: '1px',
                                }}
                                title={r.is_fallback ? 'No valid first name — click to set' : (isReadOnly ? '' : 'Click to edit')}
                              >
                                {r.is_fallback ? '"there"' : `"${r.greeting_name}"`}
                              </span>
                            )
                          ) : r.greeting_type === 'farm_name' ? (
                            <span style={{ color: '#16a34a', fontSize: '0.8rem' }}>"{r.greeting_name}"</span>
                          ) : (
                            <span style={{ color: '#888', fontSize: '0.8rem' }}>—</span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, color: '#666' }}>{r.email}</td>
                        <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#888' }}>{TYPE_LABELS[r.customer_type] || r.customer_type}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {wasSent ? (
                            <span style={{
                              display: 'inline-block', padding: '0.1rem 0.4rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600,
                              background: r.sent_status === 'opened' || r.sent_status === 'clicked' ? '#dbeafe' : r.sent_status === 'bounced' ? '#fee2e2' : '#d1fae5',
                              color: r.sent_status === 'opened' || r.sent_status === 'clicked' ? '#1e40af' : r.sent_status === 'bounced' ? '#991b1b' : '#065f46',
                            }}>
                              {r.sent_status === 'sent' || r.sent_status === 'delivered' ? 'Sent' : r.sent_status === 'opened' ? 'Opened' : r.sent_status === 'clicked' ? 'Clicked' : r.sent_status === 'bounced' ? 'Bounced' : r.sent_status}
                            </span>
                          ) : r.sent_status === 'failed' ? (
                            <span style={{ display: 'inline-block', padding: '0.1rem 0.4rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>Failed</span>
                          ) : null}
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
                {filteredRows.length === 0 && recipientSearch && (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#999', fontSize: '0.85rem' }}>
                    No recipients match &quot;{recipientSearch}&quot;
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Email Body Editor */}
          <div style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <label style={{ fontWeight: 500, fontSize: '0.9rem' }}>Email Body</label>
              <button
                onClick={() => {
                  if (!showRawHtml && editorRef.current) {
                    setData(prev => ({ ...prev, body_html: editorRef.current!.innerHTML }));
                  }
                  setShowRawHtml(!showRawHtml);
                }}
                style={{ padding: '0.25rem 0.5rem', border: '1px solid #ddd', borderRadius: '4px', background: showRawHtml ? '#2D5A27' : 'white', color: showRawHtml ? 'white' : '#333', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                {showRawHtml ? 'Visual' : 'HTML'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              {MERGE_TAGS.map(t => (
                <button
                  key={t.tag}
                  onClick={() => insertMergeTag(t.tag)}
                  disabled={isReadOnly}
                  style={{ padding: '0.25rem 0.5rem', border: '1px solid #ddd', borderRadius: '4px', background: '#f8f8f8', cursor: 'pointer', fontSize: '0.75rem', color: '#2D5A27' }}
                >
                  + {t.label}
                </button>
              ))}
            </div>

            {!showRawHtml && !isReadOnly && (
              <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
                <button onClick={() => execFormat('bold')} style={toolbarBtnStyle} title="Bold"><strong>B</strong></button>
                <button onClick={() => execFormat('italic')} style={toolbarBtnStyle} title="Italic"><em>I</em></button>
                <button onClick={() => execFormat('insertUnorderedList')} style={toolbarBtnStyle} title="Bullet List">&#8226;</button>
                <button onClick={() => execFormat('insertOrderedList')} style={toolbarBtnStyle} title="Number List">1.</button>
                <button onClick={() => {
                  const url = prompt('Enter link URL:');
                  if (url) execFormat('createLink', url);
                }} style={toolbarBtnStyle} title="Insert Link">&#128279;</button>
                <button onClick={() => execFormat('formatBlock', 'h2')} style={toolbarBtnStyle} title="Heading">H2</button>
                <button onClick={() => execFormat('formatBlock', 'h3')} style={toolbarBtnStyle} title="Subheading">H3</button>
                <button onClick={() => execFormat('formatBlock', 'p')} style={toolbarBtnStyle} title="Paragraph">P</button>
                <span style={{ width: '1px', background: '#ddd', margin: '0 2px' }} />
                <button onClick={() => setShowVideoModal(true)} style={{ ...toolbarBtnStyle, color: '#c00', fontSize: '0.8rem' }} title="Insert YouTube Video">&#9654; Video</button>
              </div>
            )}

            {showRawHtml ? (
              <textarea
                value={data.body_html}
                onChange={e => setData(prev => ({ ...prev, body_html: e.target.value }))}
                disabled={isReadOnly}
                style={{ width: '100%', minHeight: '350px', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.85rem', boxSizing: 'border-box' }}
              />
            ) : (
              <div
                ref={editorRef}
                contentEditable={!isReadOnly}
                dangerouslySetInnerHTML={{ __html: data.body_html }}
                style={{ minHeight: '350px', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.95rem', lineHeight: 1.6, outline: 'none' }}
              />
            )}
          </div>
        </div>

        {/* Right Panel — Actions */}
        <div style={{ position: 'sticky', top: '1rem' }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={handleSave}
                disabled={saving || isReadOnly}
                style={{ ...actionBtnStyle, background: 'white', color: '#333', border: '1px solid #ddd' }}
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={handleOpenSendTest}
                disabled={isReadOnly}
                style={{ ...actionBtnStyle, background: 'white', color: '#2D5A27', border: '1px solid #2D5A27' }}
              >
                Send Test Email
              </button>
              <button
                onClick={handlePreview}
                style={{ ...actionBtnStyle, background: 'white', color: '#333', border: '1px solid #ddd' }}
              >
                Preview
              </button>
              <button
                onClick={() => setShowSchedule(!showSchedule)}
                disabled={isReadOnly}
                style={{ ...actionBtnStyle, background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' }}
              >
                Schedule Send
              </button>

              {showSchedule && (
                <div style={{ padding: '0.75rem', background: '#f8f8f8', borderRadius: '4px' }}>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={e => setScheduleDate(e.target.value)}
                    style={{ width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                  />
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={e => setScheduleTime(e.target.value)}
                    style={{ width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                  />
                  <button onClick={handleSchedule} style={{ ...actionBtnStyle, background: '#1565c0', color: 'white', border: 'none', width: '100%' }}>
                    Confirm Schedule
                  </button>
                </div>
              )}

              <button
                onClick={handleSendNow}
                disabled={isReadOnly || sending || selectedCount === 0}
                style={{
                  ...actionBtnStyle,
                  background: selectedCount > 0 ? '#2D5A27' : '#ccc',
                  color: 'white',
                  border: 'none',
                  fontWeight: 600,
                }}
              >
                {sending ? 'Sending...' : selectedCount > 0 ? `Send Now (${selectedCount} recipients)` : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Enhanced Preview Modal ===== */}
      {showPreview && (() => {
        const filteredPR = previewSearch.trim()
          ? previewRecipients.filter(r =>
              r.name.toLowerCase().includes(previewSearch.toLowerCase()) ||
              r.email_address.toLowerCase().includes(previewSearch.toLowerCase())
            )
          : previewRecipients;
        const completeCount = previewRecipients.filter(r => r.has_password).length;
        const currentRecipient = previewRecipients[previewIndex];

        return (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowPreview(false)}
        >
          <div
            style={{ background: 'white', borderRadius: '8px', width: '850px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header with recipient navigation */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #eee', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0 }}>Email Preview</h3>
                <button onClick={() => setShowPreview(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }}>&times;</button>
              </div>

              {previewRecipients.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {/* Prev/Next navigation */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => { const prev = (previewIndex - 1 + previewRecipients.length) % previewRecipients.length; loadPreviewForRecipient(previewRecipients[prev].id, prev); }}
                      disabled={previewLoading}
                      style={{ padding: '0.25rem 0.5rem', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '1rem' }}
                    >&lsaquo;</button>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500, minWidth: '200px', textAlign: 'center' }}>
                      {previewLoading ? 'Loading...' : `Previewing: ${currentRecipient?.name || 'Unknown'} (${previewIndex + 1} of ${previewRecipients.length})`}
                    </span>
                    <button
                      onClick={() => { const next = (previewIndex + 1) % previewRecipients.length; loadPreviewForRecipient(previewRecipients[next].id, next); }}
                      disabled={previewLoading}
                      style={{ padding: '0.25rem 0.5rem', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '1rem' }}
                    >&rsaquo;</button>
                  </div>

                  {/* Search jump */}
                  <div style={{ position: 'relative', flex: 1, minWidth: '150px' }}>
                    <input
                      type="text"
                      placeholder="Jump to recipient..."
                      value={previewSearch}
                      onChange={e => setPreviewSearch(e.target.value)}
                      style={{ width: '100%', padding: '0.3rem 0.6rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box' }}
                    />
                    {previewSearch.trim() && filteredPR.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: 'white', border: '1px solid #ddd', borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflow: 'auto' }}>
                        {filteredPR.slice(0, 20).map(r => {
                          const idx = previewRecipients.findIndex(pr => pr.id === r.id);
                          return (
                            <div
                              key={r.id}
                              onClick={() => { loadPreviewForRecipient(r.id, idx); setPreviewSearch(''); }}
                              style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', borderBottom: '1px solid #f0f0f0' }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#f0f7ee')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                            >
                              <strong>{r.name}</strong> — {r.email_address}
                              {r.has_password && <span style={{ marginLeft: '6px', color: '#2e7d32', fontSize: '0.7rem' }}>has password</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Completeness count */}
                  <span style={{ fontSize: '0.8rem', color: '#666', whiteSpace: 'nowrap' }}>
                    {completeCount} of {previewRecipients.length} have passwords
                  </span>
                </div>
              )}
            </div>

            {/* Content area: preview + merge data */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
              {/* Email preview */}
              <div style={{ flex: 1, overflow: 'auto', borderRight: '1px solid #eee' }}>
                <iframe
                  srcDoc={previewHtml}
                  style={{ width: '100%', height: '100%', minHeight: '500px', border: 'none' }}
                  title="Email Preview"
                />
              </div>

              {/* Merge data summary sidebar */}
              {Object.keys(previewMergeData).length > 0 && (
                <div style={{ width: '220px', flexShrink: 0, padding: '1rem', overflow: 'auto', background: '#fafafa' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', color: '#333' }}>
                    Merge Data
                  </h4>
                  {Object.entries(previewMergeData).map(([key, value]) => {
                    const hasValue = Boolean(value);
                    return (
                      <div key={key} style={{ marginBottom: '0.6rem' }}>
                        <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', marginBottom: '2px' }}>
                          {hasValue ? '\u2705' : '\u26A0\uFE0F'} {key}
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: hasValue ? '#333' : '#d97706',
                          fontWeight: hasValue ? 400 : 500,
                          wordBreak: 'break-all',
                        }}>
                          {hasValue ? (key === 'password' ? value.substring(0, 4) + '****' : value) : 'Not set'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* ===== Video Insert Modal ===== */}
      {showVideoModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: '8px', padding: '1.5rem', width: '480px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Insert YouTube Video</h3>
              <button onClick={() => { setShowVideoModal(false); setVideoUrl(''); setVideoError(''); }} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#999' }}>
                &times;
              </button>
            </div>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#666' }}>
              Paste a YouTube URL. A clickable thumbnail with play button will be generated and inserted into your email.
            </p>
            {videoError && (
              <div style={{ padding: '0.5rem 0.75rem', background: '#fce4ec', color: '#c62828', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                {videoError}
              </div>
            )}
            <input
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              value={videoUrl}
              onChange={e => setVideoUrl(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleInsertVideo(); } }}
              autoFocus
              style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '1rem' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowVideoModal(false); setVideoUrl(''); setVideoError(''); }}
                style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                Cancel
              </button>
              <button
                onClick={handleInsertVideo}
                disabled={videoLoading || !videoUrl.trim()}
                style={{
                  padding: '0.5rem 1rem', border: 'none', borderRadius: '4px', background: '#c00', color: 'white',
                  cursor: videoLoading || !videoUrl.trim() ? 'not-allowed' : 'pointer', fontSize: '0.9rem',
                  opacity: videoLoading || !videoUrl.trim() ? 0.6 : 1,
                }}
              >
                {videoLoading ? 'Generating...' : 'Insert Video'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Send Test Modal ===== */}
      {showTestModal && (() => {
        const filteredTR = testSearch.trim()
          ? testRecipients.filter(r =>
              r.name.toLowerCase().includes(testSearch.toLowerCase()) ||
              r.email_address.toLowerCase().includes(testSearch.toLowerCase())
            )
          : testRecipients;

        return (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: '8px', padding: '1.5rem', width: '500px', maxHeight: '80vh',
            display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Send Test Email</h3>
              <button onClick={() => setShowTestModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#999' }}>
                &times;
              </button>
            </div>

            <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', color: '#666' }}>
              Send a real email (with [TEST] prefix) to a campaign recipient. Merge fields will be filled in.
            </p>

            {testSuccess && (
              <div style={{
                padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.85rem',
                background: testSuccess.startsWith('Failed') ? '#fce4ec' : '#e8f5e9',
                color: testSuccess.startsWith('Failed') ? '#c62828' : '#2e7d32',
              }}>
                {testSuccess}
              </div>
            )}

            {testRecipients.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#999', padding: '1rem' }}>
                No recipients found. Load recipients first, then save the campaign.
              </p>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Search recipients by name or email..."
                  value={testSearch}
                  onChange={e => setTestSearch(e.target.value)}
                  style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', marginBottom: '0.75rem' }}
                />

                <div style={{ overflowY: 'auto', flex: 1, maxHeight: '400px' }}>
                  {filteredTR.slice(0, 50).map(r => (
                    <div key={r.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '0.5rem 0.5rem', borderBottom: '1px solid #f0f0f0',
                    }}>
                      <div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                          {r.name}
                          {r.has_password && (
                            <span style={{ marginLeft: '6px', fontSize: '0.6rem', padding: '1px 5px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '8px' }}>
                              password
                            </span>
                          )}
                          {r.has_merge_fields && !r.has_password && (
                            <span style={{ marginLeft: '6px', fontSize: '0.6rem', padding: '1px 5px', background: '#fff3e0', color: '#e65100', borderRadius: '8px' }}>
                              no password
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>{r.email_address}</div>
                      </div>
                      <button
                        onClick={() => handleSendTestToRecipient(r.id, r.name)}
                        disabled={testSending === r.id}
                        style={{
                          padding: '0.3rem 0.75rem', background: '#2D5A27', color: 'white',
                          border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                          opacity: testSending === r.id ? 0.6 : 1,
                        }}
                      >
                        {testSending === r.id ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  ))}
                  {filteredTR.length === 0 && (
                    <p style={{ textAlign: 'center', color: '#999', padding: '1rem' }}>No recipients match</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
};

// ===== Styles =====

const toolbarBtnStyle: React.CSSProperties = {
  padding: '0.3rem 0.5rem',
  border: '1px solid #ddd',
  borderRadius: '3px',
  background: 'white',
  cursor: 'pointer',
  fontSize: '0.85rem',
  minWidth: '30px',
};

const actionBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1rem',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  width: '100%',
};

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.6rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.8rem',
  color: '#4b5563',
  borderBottom: '2px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderBottom: '1px solid #f0f0f0',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '250px',
};

export default CampaignCompose;
